import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import classnames from "classnames";
import { Link, matchPath, useLocation } from "react-router-dom";

type SubMenuLink = {
  label: string;
  key: string;
  /** When set, shows a red alert icon after the label with this tooltip. */
  alertTooltip?: string;
};

type CIQSubTabsProps = {
  menuLinks?: SubMenuLink[];
};

const verticalCenterClassname = "flex items-center";
const linkClassname =
  "text-[black] hover:text-[black] hover:[text-shadow:0_0_0.3px_rgba(0,0,0,1)]";
const activeLinkClassname =
  "text-ciq-primary-900 border-solid border-ciq-primary-900 border-x-0 border-t-0 border-b-2 font-semibold";

/**
 * Second-level tab row, nested under CIQTabs. Same active/inactive treatment as
 * the parent row but shorter and a size smaller so the hierarchy reads.
 */
function CIQSubTabs(props: CIQSubTabsProps) {
  const { menuLinks = [] } = props;
  const { pathname } = useLocation();

  if (!menuLinks.length) return null;

  return (
    <nav className="bg-transparent leading-none hide-on-print px-4 border-solid border-x-0 border-t-0 border-b border-slate-100">
      <div className="h-[34px] flex items-stretch gap-x-4 text-[13px]">
        {menuLinks.map((link) => {
          const className = classnames(verticalCenterClassname, {
            [activeLinkClassname]: matchPath(pathname, {
              path: link.key,
              exact: true
            })
          });
          return (
            <Link
              key={link.key}
              to={link.key}
              className={`${verticalCenterClassname} items-stretch ${linkClassname}`}
              data-testid="ciq-sub-tabs-link"
            >
              <div className={classnames(className, "gap-x-1.5")}>
                {link.label}
                {link.alertTooltip ? (
                  <Tooltip title={link.alertTooltip} placement="bottom">
                    <span className="inline-flex shrink-0 items-center justify-center w-5 h-5 overflow-visible">
                      <ExclamationCircleOutlined
                        className="text-ciq-red-700 text-[16.67px] leading-none align-middle [&_svg]:block [&_svg]:overflow-visible"
                        style={{ color: "var(--ciq-red-700)" }}
                        data-testid="ciq-sub-tab-alert"
                        aria-label={link.alertTooltip}
                      />
                    </span>
                  </Tooltip>
                ) : null}
              </div>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default CIQSubTabs;
